import React, {useState, useMemo, useEffect, useCallback, useRef} from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import {css, Global} from '@emotion/react'
import {Box, useMultiStyleConfig, Text} from '@chakra-ui/react'
import {
    ShopperBasketsMutations,
    ShopperOrdersMutations,
    useShippingMethodsForShipment,
    useShopperOrdersMutation,
    useShopperBasketsMutation
} from '@salesforce/commerce-sdk-react'
import adyenCss from '@adyen/adyen-web/styles/adyen.css'

import useNavigation from '../../hooks/use-navigation'
import {useCurrentBasket} from '../../hooks/use-current-basket'
import useOcapiSite from '../../hooks/useOcapiSite'
import {CHECKOUT_TOKEN, CART_HREF, API_ERROR_MESSAGE, CHECKOUT_ORDER_ID} from '../../constants'
import LoadingSpinner from '../../components/loading-spinner'
import useEffectEvent from '../../hooks/use-effect-event'
import logger from '../../utils/logger-instance'
import {convertToAdyenFormat} from '../../utils/adyen-utils'
import useAdyenCheckout from '../../hooks/use-adyen-checkout'
import useApplePay from '../../hooks/use-apple-pay'
import PropTypes from 'prop-types'
import {useCountry} from '../../hooks'
import {trackApplePayClick} from '../../tracking/analytics'
import Link from '../link'
import useAddressValidator from '../../google-maps-api/hooks/useAddressValidator'

/**
 * Checks if the GOOGLE address is valid or the validation should be skipped.
 * @param {Object} googleAddress is the GOOGLE address that is going to be validated
 * @param {Function} callback that is going to execute request for address validation
 * @param {Boolean} isValidationEnabled is indicator if the GOOGLE validation should be executed or skipped
 * @returns {Boolean} true if the address is valid or in case there should be no validation
 */
const isAddressValid = async (googleAddress, addressValidator, isValidationEnabled) => {
    if (!isValidationEnabled) {
        return true
    }
    const validationResult = await addressValidator({
        address: googleAddress
    })
    return !!validationResult?.result?.verdict?.addressComplete
}

/**
 * Gets mapped GOOGLE address from the APPLEPAY address. We skip all empty strings because they lead to invalid address validation.
 * @param {Object} appleAddress is the APPLEPAY address that is going to be transformed into GOOGLE one
 * @returns {Object} the GOOGLE address
 */
const getGoogleAddress = (appleAddress) => {
    const result = {}
    if (appleAddress.countryCode) {
        result.regionCode = appleAddress.countryCode
    }
    if (appleAddress.postalCode) {
        result.postalCode = appleAddress.postalCode
    }
    if (appleAddress.administrativeArea) {
        result.administrativeArea = appleAddress.administrativeArea
    }
    if (appleAddress.locality) {
        result.locality = appleAddress.locality
    }
    if (appleAddress.subLocality) {
        result.sublocality = appleAddress.subLocality
    }
    if (appleAddress.addressLines) {
        result.addressLines = appleAddress.addressLines
    }
    return result
}

const getBasketAddress = (appleAddress) => {
    const firstName = appleAddress.givenName || appleAddress.phoneticGivenName
    const lastName = appleAddress.familyName || appleAddress.phoneticFamilyName
    let fullName = ''
    if (firstName) {
        if (lastName) {
            fullName = firstName + ' ' + lastName
        } else {
            fullName = firstName
        }
    } else if (lastName) {
        fullName = lastName
    }
    return {
        first_name: firstName,
        last_name: lastName,
        full_name: fullName,
        address1: appleAddress?.addressLines?.length > 0 ? appleAddress.addressLines[0] : '',
        address2: appleAddress?.addressLines?.length > 1 ? appleAddress.addressLines[1] : '',
        city: appleAddress.locality,
        country_code: appleAddress.countryCode,
        state_code: appleAddress.administrativeArea,
        postal_code: appleAddress.postalCode
    }
}

const getLineItems = (productItems) => {
    return productItems?.map((item) => {
        return {
            label: item.itemText,
            amount: item.price,
            type: 'final'
        }
    })
}

/**
 * @param {boolean} [enabled] - optional flag to enable/disable ApplePay
 * @param {number} [basketAmount] - basket amount stored in Adyen session
 * @returns {React.JSX.Element|null}
 * @constructor
 */
const ApplePay = ({enabled, basketAmount}) => {
    const addressValidator = useAddressValidator()
    const country = useCountry()
    const navigate = useNavigation()
    const {isExpressPaymentAddressValidationEnabled} = useOcapiSite()
    const {formatMessage, formatNumber} = useIntl()
    const [container, setContainer] = useState()
    const adyenStyles = useMultiStyleConfig('Adyen')
    const isApplePayMountedRef = useRef(false)

    const {
        data: basket,
        isInitialLoading: isBasketLoading,
        refetch: refetchBaskets
    } = useCurrentBasket()
    const updateCustomerForBasket = useShopperBasketsMutation(
        ShopperBasketsMutations.UpdateCustomerForBasket
    )
    const updateShippingAddressForShipment = useShopperBasketsMutation(
        ShopperBasketsMutations.UpdateShippingAddressForShipment
    )
    const updateBillingAddressForBasket = useShopperBasketsMutation(
        ShopperBasketsMutations.UpdateBillingAddressForBasket
    )
    const addPaymentInstrumentToBasket = useShopperBasketsMutation(
        ShopperBasketsMutations.AddPaymentInstrumentToBasket
    )
    const updateShippingMethodForShipment = useShopperBasketsMutation(
        ShopperBasketsMutations.UpdateShippingMethodForShipment
    )
    const createOrder = useShopperOrdersMutation(ShopperOrdersMutations.CreateOrder)
    const {data: shippingMethods} = useShippingMethodsForShipment(
        {
            parameters: {
                basketId: basket?.basketId,
                shipmentId: basket?.shipments?.[0]?.shipmentId
            }
        },
        {
            enabled: !!basket?.shipments?.[0]?.shipmentId && enabled !== false
        }
    )

    const getShippingMethod = useCallback(
        (method, isSelected) => {
            return {
                identifier: method.id,
                label:
                    method.name +
                    ' - ' +
                    formatNumber(method.price, {
                        style: 'currency',
                        currency: basket?.currency
                    }),
                amount: method.price,
                // only if this field is set then the shipping method would be kept as selected
                // if it isn't then applepay selects the first shipping method
                selected: isSelected,
                detail:
                    method.description +
                    (method.c_estimatedArrivalTime
                        ? ' ' +
                          formatMessage(
                              {
                                  id: 'checkout.shipping.delivery_time',
                                  defaultMessage: 'Delivery: {time}',
                                  description:
                                      'The date range when delivery is expected to happen. {time} contains range like "24.11.2021 - 26.11.2021"'
                              },
                              {time: method.c_estimatedArrivalTime}
                          )
                        : '')
            }
        },
        [basket?.currency, formatMessage, formatNumber]
    )

    const getShippingMethods = useCallback(
        (shippingMethods, selectedId) => {
            return shippingMethods?.applicableShippingMethods?.map((method) => {
                return getShippingMethod(method, method.id === selectedId)
            })
        },
        [getShippingMethod]
    )

    // ApplePay event handlers
    const onApplePayAuthorized = useEffectEvent(async (data, {resolve, reject}) => {
        try {
            const payment = data?.authorizedEvent?.payment
            let updatedBasket = basket
            if (payment?.shippingContact) {
                const shippingAddress = getBasketAddress(payment.shippingContact)
                if (shippingAddress.country_code !== country) {
                    reject(
                        new global.ApplePayError(
                            'shippingContactInvalid',
                            'countryCode',
                            formatMessage({
                                defaultMessage: 'Delivery strictly limited to country',
                                id: 'global.error.delivery_country_limited'
                            })
                        )
                    )
                    return
                }
                const validAddress = await isAddressValid(
                    getGoogleAddress(payment.shippingContact),
                    addressValidator,
                    isExpressPaymentAddressValidationEnabled
                )
                if (!validAddress) {
                    reject(
                        new global.ApplePayError(
                            'shippingContactInvalid',
                            'countryCode',
                            formatMessage({
                                defaultMessage: 'Invalid delivery address',
                                id: 'global.error.delivery_address_invalid'
                            })
                        )
                    )
                    return
                }
                updatedBasket = await updateShippingAddressForShipment.mutateAsync({
                    body: shippingAddress,
                    parameters: {
                        basketId: basket?.basketId,
                        shipmentId: updatedBasket?.shipments?.[0]?.shipmentId,
                        useAsBilling: true
                    }
                })
                logger.log('shippingAddressUpdate', updatedBasket)
                if (payment?.shippingContact?.emailAddress) {
                    updatedBasket = await updateCustomerForBasket.mutateAsync({
                        parameters: {basketId: basket?.basketId},
                        body: {
                            email: payment.shippingContact.emailAddress
                        }
                    })
                    logger.log('customerUpdate', updatedBasket)
                }
            }
            if (payment?.billingContact) {
                const billingAddress = getBasketAddress(payment.billingContact)
                if (billingAddress.country_code !== country) {
                    reject(
                        new global.ApplePayError(
                            'billingContactInvalid',
                            'countryCode',
                            formatMessage({
                                defaultMessage: 'Delivery strictly limited to country',
                                id: 'global.error.delivery_country_limited'
                            })
                        )
                    )
                    return
                }

                updatedBasket = await updateBillingAddressForBasket.mutateAsync({
                    body: billingAddress,
                    parameters: {
                        basketId: basket?.basketId
                    }
                })
                logger.log('billingAddressUpdate', updatedBasket)
            }
            updatedBasket = await addPaymentInstrumentToBasket.mutateAsync({
                parameters: {basketId: basket?.basketId},
                body: {
                    amount: updatedBasket?.orderTotal || updatedBasket?.productTotal,
                    payment_method_id: 'AdyenComponent'
                }
            })
            logger.log('paymentInstrumentUpdate', updatedBasket)
            // compares Adyen payment amount with basket amount in order not to proceed SFCC order and authorize payment with different amounts
            const sfccAmountInAdyenFormat = convertToAdyenFormat(
                updatedBasket?.orderTotal,
                updatedBasket?.currency
            )
            if (sfccAmountInAdyenFormat !== basketAmount) {
                const wrongAmountError = formatMessage({
                    defaultMessage:
                        'There was an update of the basket total due to tax changes. Please, close the modal and try to place the order again!',
                    id: 'global.error.invalid_basket_amount'
                })
                reject(new global.ApplePayError('unknown', 'country', wrongAmountError))
                return
            }
            const merchantReference = basket?.basketId
            const applePayResult = JSON.stringify(payment?.token?.paymentData)
            const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
            const result = await createOrder.mutateAsync({
                body: {
                    basketId: merchantReference
                },
                parameters: {
                    c_applePayToken: applePayResult,
                    c_cancellationEndTimeInUserTz: timeZone,
                    c_Adyen_merchantReference: merchantReference
                }
            })
            if (!result.orderNo) {
                const message = formatMessage({
                    defaultMessage: 'Order was cancelled because payment has failed/refused',
                    id: 'global.error.payment.failed'
                })
                reject(new global.ApplePayError('unknown', 'country', message))
                refetchBaskets()
                navigate(CART_HREF)
            }
            // TODO figure another way to pass the merchant reference and order id to onAdyenPaymentResult in useAdyenCheckout
            window.localStorage.setItem(CHECKOUT_TOKEN, merchantReference)
            // on PRD the confirmation page is unable to load the order id using the merchantReference
            // so we also store the order id in the session storage
            window.localStorage.setItem(CHECKOUT_ORDER_ID, result.orderNo)
            resolve()
        } catch (error) {
            logger.error('failure while handling ApplePay payment', error)
            reject(new global.ApplePayError('unknown', 'country', formatMessage(API_ERROR_MESSAGE)))
        }
    })

    const onApplePayShippingContactSelected = useEffectEvent(async (resolve, reject, event) => {
        try {
            if (event?.shippingContact) {
                const body = getBasketAddress(event.shippingContact)

                if (body.country_code !== country) {
                    const update = {
                        newTotal: {
                            label: formatMessage({
                                defaultMessage: 'Estimated Total',
                                id: 'order_summary.label.estimated_total'
                            }),
                            amount: (basket?.orderTotal || basket?.productTotal)?.toString()
                        },
                        errors: [
                            new global.ApplePayError(
                                'shippingContactInvalid',
                                'countryCode',
                                formatMessage({
                                    defaultMessage: 'Delivery strictly limited to country',
                                    id: 'global.error.delivery_country_limited'
                                })
                            )
                        ]
                    }
                    reject(update)
                    return
                }

                let resultBasket = await updateShippingAddressForShipment.mutateAsync({
                    body,
                    parameters: {
                        basketId: basket?.basketId,
                        shipmentId: basket?.shipments?.[0]?.shipmentId,
                        useAsBilling: true
                    }
                })
                if (event?.shippingContact?.emailAddress) {
                    resultBasket = await updateCustomerForBasket.mutateAsync({
                        parameters: {basketId: basket?.basketId},
                        body: {
                            email: event.shippingContact.emailAddress
                        }
                    })
                }
                const lineItems = getLineItems(resultBasket?.productItems)
                if (resultBasket?.shipments?.[0]?.shippingMethod) {
                    lineItems.push(
                        getShippingMethod(resultBasket?.shipments?.[0]?.shippingMethod, true)
                    )
                }
                const update = {
                    newTotal: {
                        label: formatMessage({
                            defaultMessage: 'Estimated Total',
                            id: 'order_summary.label.estimated_total'
                        }),
                        amount: (resultBasket?.orderTotal || resultBasket?.productTotal)?.toString()
                    },
                    newLineItems: lineItems,
                    newShippingMethods: getShippingMethods(
                        shippingMethods,
                        resultBasket?.shipments?.[0]?.shippingMethod?.id
                    )
                }
                resolve(update)
            } else {
                reject()
            }
        } catch (e) {
            logger.error('error while handling shipping contact update', e)
            reject({
                errors: [
                    {
                        code: 'unknown',
                        message: formatMessage(API_ERROR_MESSAGE)
                    }
                ]
            })
        }
    })
    const onApplePayMethodSelected = useEffectEvent(async (resolve, reject, event) => {
        try {
            if (event?.shippingMethod?.identifier) {
                const resultBasket = await updateShippingMethodForShipment.mutateAsync({
                    body: {id: event?.shippingMethod?.identifier},
                    parameters: {
                        basketId: basket?.basketId,
                        shipmentId: basket?.shipments?.[0]?.shipmentId
                    }
                })
                const lineItems = getLineItems(resultBasket.productItems)
                lineItems.push(event?.shippingMethod)
                const update = {
                    newTotal: {
                        label: formatMessage({
                            defaultMessage: 'Estimated Total',
                            id: 'order_summary.label.estimated_total'
                        }),
                        amount: (resultBasket?.orderTotal || resultBasket?.productTotal)?.toString()
                    },
                    newLineItems: lineItems,
                    newShippingMethods: getShippingMethods(
                        shippingMethods,
                        event?.shippingMethod?.identifier
                    )
                }
                resolve(update)
            } else {
                reject()
            }
        } catch {
            reject()
        }
    })

    const onApplePayButtonClick = useEffectEvent(() => trackApplePayClick(basket))

    // prepare ApplePay configuration - it should not change during the component lifecycle
    const applePayConfiguration = useMemo(
        () => ({
            onAuthorized: onApplePayAuthorized,
            onShippingContactSelected: onApplePayShippingContactSelected,
            onShippingMethodSelected: onApplePayMethodSelected,
            onClick: (resolve) => {
                onApplePayButtonClick()
                resolve()
            }
        }),
        [
            onApplePayAuthorized,
            onApplePayShippingContactSelected,
            onApplePayMethodSelected,
            onApplePayButtonClick
        ]
    )

    const {checkout} = useAdyenCheckout({enabled, requiredMethod: 'applepay'})
    const {applePay} = useApplePay({checkout, config: applePayConfiguration, enabled})

    // mount the ApplePay element when it's available, enabled and the container is set
    useEffect(() => {
        if (applePay && enabled !== false && container) {
            applePay.mount(container)
            isApplePayMountedRef.current = true
            return () => {
                applePay.unmount()
                isApplePayMountedRef.current = false
            }
        }
    }, [applePay, enabled, container])

    // no render if not enabled or not available
    if (enabled === false || !applePay) {
        return null
    }

    return (
        <>
            <Global
                styles={css`
                    ${adyenCss}
                `}
            />
            {isBasketLoading ? (
                <LoadingSpinner />
            ) : (
                <>
                    <Box
                        ref={setContainer}
                        {...adyenStyles.container}
                        data-testid="apple-payment-method-container"
                        id="apple-payment-method-container"
                    />
                    <Text
                        textStyle="Regular Small"
                        color="stokkeGray.accessibleText"
                        paddingTop={6}
                    >
                        <FormattedMessage
                            defaultMessage="By clicking Apple Pay for your express checkout, you confirm and agree to our {termsAndConditions} for the purchase of your order. Our {privacyPolicy} will apply."
                            id="forms.marketing.applePay"
                            values={{
                                termsAndConditions: (
                                    <Link
                                        href="/customer-service/legal/terms/terms.html"
                                        isExternal
                                        size="sm"
                                        color="stokkeCore.accessibleGrey"
                                    >
                                        <FormattedMessage
                                            defaultMessage="Terms and Conditions"
                                            id="footer.link.terms_conditions"
                                        />
                                    </Link>
                                ),
                                privacyPolicy: (
                                    <Link
                                        href="/customer-service/legal/privacy-policy.html"
                                        isExternal
                                        size="sm"
                                        color="stokkeCore.accessibleGrey"
                                    >
                                        <FormattedMessage
                                            defaultMessage="Privacy Policy"
                                            id="footer.link.privacy_policy"
                                        />
                                    </Link>
                                )
                            }}
                        />
                    </Text>
                </>
            )}
        </>
    )
}

ApplePay.propTypes = {
    basketAmount: PropTypes.number,
    enabled: PropTypes.bool
}

export default ApplePay
